/**
 * @fileoverview Bohomoon Charm Builder web components and utilities.
 * @module boho-charm-builder
 *
 * @summary Contents
 *  - {@link formatMoney} — formats currency values with delimiters and patterns.
 *  - {@link CharmBuilder} — Main builder wrapper; reset + counter logic.
 *  - {@link CharmParentOption} — Parent charm slot & drawer lifecycle.
 *  - {@link CharmGroupOption} — Group drawer for browsing charms.
 *  - {@link CharmOption} — Selectable charm option (radio), extends HTMLInputElement.
 *  - {@link SelectedCharm} — Selected charm chip; removal & re-edit.
 *  - {@link CharmFilter} — Drawer filter (e.g. color), synced to session/cart.
 *
 */

/**
 * @typedef {Object} CharmSelection
 * @property {string} id - Unique ID linking the selection to its parent slot.
 * @property {string} title - Product title of the charm.
 * @property {string|number} variantId - Shopify Variant ID of the charm.
 * @property {string|number} productId - Shopify Product ID of the charm.
 * @property {number} price - Price of the charm (in cents / minor units).
 * @property {string} groupId - Group/metaobject id the charm belongs to.
 */

/**
 * Custom event fired when a charm is selected.
 * @event charm:selected
 * @type {CustomEvent<{ uniqueId: string, index: number, variant: object, product: object }>}
 */

/**
 * Custom event fired after the builder performs a full reset.
 * @event charm:reset
 * @type {CustomEvent<void>}
 */

/**
 * Custom event fired when a previously selected charm is removed.
 * @event charm:removed
 * @type {CustomEvent<{ index: number }>}
 */

/**
 * Formats a money value in cents using the theme’s money format.
 * @function formatMoney
 * @param {(number|string)} cents
 * @param {string} [format]
 * @returns {string}
 */
function formatMoney(cents, format = '') {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/,
    formatString = format || translationStrings.settings.moneyFormat;
  function defaultTo(value2, defaultValue) {
    return value2 === null || value2 !== value2 ? defaultValue : value2;
  }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ',');
    decimal = defaultTo(decimal, '.');
    if (isNaN(number) || number === null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    let parts = number.split('.'),
      dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        '$1' + thousands,
      ),
      centsAmount = parts[1] ? decimal + parts[1] : '';
    return dollarsAmount + centsAmount;
  }
  let value = '';
  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', '.');
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ');
      break;
    case 'amount_no_decimals_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 0, "'");
      break;
  }
  if (formatString.indexOf('with_comma_separator') !== -1) {
    return formatString.replace(placeholderRegex, value);
  } else {
    return formatString.replace(placeholderRegex, value);
  }
}

/** @type {{ settings: any, routes: any, customiser: any }} */
const translationStrings = window.themeVariables;

/** @type {string} */
const currencyFormat = translationStrings.settings.currencyCodeEnabled
  ? translationStrings.settings.moneyWithCurrencyFormat
  : translationStrings.settings.moneyFormat;

document.addEventListener('variant:changed', (evt) => {
  const selectedVariantJson = document.getElementById('ProductData');
  if (!selectedVariantJson) return;

  selectedVariantJson.textContent = JSON.stringify(evt.detail.variant);
});

/**
 * Main builder wrapper: orchestrates reset, animation, and selection counter.
 * @class CharmBuilder
 * @extends HTMLElement
 * @listens variant:changed
 * @listens cart:updated
 * @fires charm:reset
 */
class CharmBuilder extends HTMLElement {
  constructor() {
    super();

    this.counter = this.querySelector('[data-charm-counter]');
  }

  connectedCallback() {
    CharmBuilder.instance = this;
    this.scrollBuilderIntoView();

    document.addEventListener('variant:changed', (evt) => {
      this.dataset.bundleParentId = evt.detail.variant.id;
      this.dataset.bundleParentPrice = evt.detail.variant.price;
    });

    document.addEventListener('cart:updated', (evt) => {
      if (!evt.detail.hasCharms) return;

      this.resetCharmBuilder();
    });
  }

  resetCharmBuilder() {
    // Fade out, perform reset, then fade back in to avoid visual jolts
    const el = this;
    const performFullReset = () => this.resetStateAndUI();

    // If user prefers reduced motion, just do the reset
    const prefersReduced =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      performFullReset();
      return;
    }

    // Freeze current height to avoid a jolt when parents are removed
    const startHeight = el.offsetHeight;
    el.style.maxHeight = `${startHeight}px`;
    el.style.overflow = 'hidden';

    // Start fade-out
    el.setAttribute('aria-busy', 'true');
    el.setAttribute('data-resetting', '');

    // Wait for the transition to finish (or fall back to a timeout)
    const onTransitionEnd = (evt) => {
      if (evt.target !== el) return;
      el.removeEventListener('transitionend', onTransitionEnd);
      // Perform the actual reset while faded
      let endHeight;

      try {
        // Perform the actual reset while frozen
        performFullReset();

        // Measure the new natural height after DOM changes
        endHeight = el.scrollHeight;

        // Animate from the frozen height to the new height
        // (this triggers the height transition we defined in CSS)
        requestAnimationFrame(() => {
          // Force reflow so the height transition reliably fires
          void el.offsetHeight;
          el.style.maxHeight = `${endHeight}px`;
        });
      } finally {
        // Next frame: remove the flag to fade back in
        requestAnimationFrame(() => {
          el.removeAttribute('data-resetting');
          el.removeAttribute('aria-busy');
        });

        // Once the HEIGHT transition completes, clear inline styles
        const startPx = parseFloat(el.style.maxHeight) || 0;
        const clearInlineHeight = () => {
          el.style.maxHeight = '';
          el.style.overflow = '';
        };
        const onHeightEnd = (evt) => {
          if (evt.target !== el || evt.propertyName !== 'max-height') return;
          el.removeEventListener('transitionend', onHeightEnd);
          clearInlineHeight();
        };
        el.addEventListener('transitionend', onHeightEnd);
        // If height didn't actually change, or the event gets swallowed, ensure cleanup
        const endPx = endHeight || el.scrollHeight;
        if (endPx === startPx) {
          // No height delta → no height transition → clear immediately next frame
          requestAnimationFrame(clearInlineHeight);
        } else {
          // Last‑ditch timeout; clears even if transitionend never arrives
          setTimeout(() => {
            // If still set, clear it
            if (el.style.maxHeight) clearInlineHeight();
          }, 600);
        }
      }
    };

    // If the element has a transition, listen for it; otherwise, use a fallback timer
    let fallbackId;
    const computed = getComputedStyle(el);
    const duration =
      (parseFloat(computed.transitionDuration) || 0) +
      (parseFloat(computed.transitionDelay) || 0);

    if (duration > 0) {
      el.addEventListener('transitionend', onTransitionEnd, { once: true });
    } else {
      fallbackId = setTimeout(() => {
        // No transition support detected; still respect the height freeze
        el.setAttribute('data-reset-phase', 'mutate');
        try {
          performFullReset();

          const endHeight = el.scrollHeight;
          // Manually step height change to give a smoother collapse even without transitions
          requestAnimationFrame(() => {
            el.style.maxHeight = `${endHeight}px`;
          });
        } finally {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // el.removeAttribute('data-reset-phase');
              el.removeAttribute('data-resetting');
              el.removeAttribute('aria-busy');

              // Cleanup inline styles
              el.style.maxHeight = '';
              el.style.overflow = '';
            });
          });
        }
      }, 600);
    }

    // Safety: clear fallback if transitionend fires
    el.addEventListener(
      'transitionend',
      () => {
        if (fallbackId) clearTimeout(fallbackId);
      },
      { once: true },
    );
  }

  /**
   * Clears in-memory state and UI, reinstates first parent slot, and updates prices.
   * Also dispatches the {@link event:charm:reset} event when finished.
   * @fires charm:reset
   */
  resetStateAndUI() {
    // 1) Clear in‑memory state
    CharmOption.selectedCharms = [];
    CharmOption.limitReached = false;

    // 2) Update the JSON <script> tag (this is the bit you’re missing)
    const scriptTag = document.getElementById('selected-charms');
    if (scriptTag) {
      scriptTag.textContent = JSON.stringify({ selectedCharms: [] });
    }

    // Begin builder fade-out during reset
    this.setAttribute('aria-busy', 'true');
    this.setAttribute('data-resetting', '');

    // 3) Reset the UI list
    const selectedHolder = document.getElementById('SelectedCharms');
    if (selectedHolder) {
      selectedHolder.innerHTML = '';
      selectedHolder.setAttribute('aria-hidden', 'true');
    }

    // 4) Reset parent selectors: keep the first, remove others, restore labels
    const allParents = this.querySelectorAll(
      'charm-parent-option[data-primary-parent]',
    );
    if (allParents.length) {
      const firstParent = allParents[0];

      // Remove dynamically-added parents
      for (let i = 1; i < allParents.length; i++) allParents[i].remove();

      // Restore the first parent’s defaults
      firstParent.classList.remove('selected');
      firstParent.setAttribute('data-unique-id', crypto.randomUUID());
      firstParent.setAttribute('data-index', '0');
      firstParent.setAttribute('charm-drawer', 'charm-drawer-0');

      const btn = firstParent.querySelector('[data-charm-parent-button]');
      if (btn) {
        btn.removeAttribute('disabled');
        btn.setAttribute('tabindex', '0');
        btn.setAttribute('aria-controls', 'charm-drawer-0');
      }

      // Put the default label/price back from SelectorStrings[0]
      const selectorStringsEl = document.getElementById('SelectorStrings');
      if (selectorStringsEl) {
        try {
          const strings = JSON.parse(selectorStringsEl.textContent);
          const labelEl = firstParent.querySelector('[data-charm-label]');
          const priceEl = firstParent.querySelector('[data-charm-price]');
          if (strings['0']) {
            if (labelEl) labelEl.textContent = strings['0'].charmOrdinal;
            if (priceEl) priceEl.textContent = strings['0'].charmPrice;
          }
        } catch (e) {
          console.warn('Failed to parse SelectorStrings during reset', e);
        }
      }
    }

    // 5) Uncheck any selected radios in live drawers/templates
    document.querySelectorAll('input[is="charm-option"]').forEach((input) => {
      input.checked = false;
      input.removeAttribute('checked');
    });

    // 6) Reset the bundle reference back to its default
    if (CharmOption.primaryBundleReferenceInput) {
      CharmOption.primaryBundleReferenceInput.value =
        CharmOption.defaultBundleReference;
    }

    // 7) Prices, visibility, and counter
    try {
      CharmOption.updateCharmPrices();
    } catch { /* empty */ }
    try {
      CharmOption.toggleFormVisibility();
    } catch { /* empty */ }
    try {
      CharmBuilder.updateCounter(0);
    } catch { /* empty */ }

    // 8) Hook for anything else
    document.dispatchEvent(new CustomEvent('charm:reset', { bubbles: true }));
  }

  static updateCounter(count) {
    const instance = CharmBuilder.getInstance();

    instance.counter.textContent = count;
  }

  static getInstance() {
    return CharmBuilder.instance;
  }

  static getCharmLimit() {
    return parseInt(CharmBuilder.getInstance().dataset.charmLimit);
  }

  scrollBuilderIntoView() {
    document
      .querySelector('[data-charm-scroller]')
      .addEventListener('click', () =>
        this.scrollIntoView({ behavior: 'smooth', block: 'center' }),
      );
  }
}

if (!customElements.get('charm-builder')) {
  customElements.define('charm-builder', CharmBuilder);
}

/**
 * Handles a single “parent” charm slot and its drawer lifecycle.
 * @class CharmParentOption
 * @extends HTMLElement
 * @listens charm:selected
 * @listens charm:removed
 */
class CharmParentOption extends HTMLElement {
  constructor() {
    super();

    this.buttonToggle = this.querySelector('[data-charm-parent-button]');
    this.externalDrawerButtons = document.querySelectorAll('[data-charm-external-button]')
    this.productTitleLabel = this.querySelector('[data-charm-label]');
    this.productPriceLabel = this.querySelector('[data-charm-price]');
    this.drawerTitle = this.productTitleLabel?.textContent;
    this.charmBuilder = CharmBuilder.getInstance();

    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.buttonToggle.addEventListener('click', this.handleClick);

    this.externalDrawerButtons.forEach((button) => {
      button.addEventListener('click', this.handleClick);
    })
  }

  handleClick() {
    let drawer =
      this.charmBuilder?.querySelector(`[data-id="${this.charmDrawer}"]`) ||
      document.querySelector(`[data-id="${this.charmDrawer}"]`);

    if (!drawer) {
      drawer = this.createDrawer();
    }

    if (this.hasAttribute('show-back')) {
      const backButton = drawer.querySelector('[data-action="close-single"]');
      if (backButton) backButton.hidden = false;
    }

    drawer.open = true;
  }

  createDrawer() {
    const wrapperTemplate = document.getElementById('drawer-wrapper-template');
    const contentTemplate = document.getElementById(this.charmDrawer);
    if (!wrapperTemplate || !contentTemplate) return;

    const wrapperClone = wrapperTemplate.content.cloneNode(true);
    document.body.appendChild(wrapperClone);

    const drawer = document.querySelector('charm-drawer:last-of-type');

    drawer.dataset.id = this.charmDrawer;

    const createDrawerEvent = new CustomEvent('drawer:created', {
      detail: {
        drawer,
        filter: sessionStorage.getItem('charm-filter'),
      },
    });

    const contentClone = contentTemplate.content.cloneNode(true);

    document.dispatchEvent(createDrawerEvent);
    drawer.querySelector('[data-drawer-content]').appendChild(contentClone);
    drawer.querySelector('[data-drawer-title]').textContent = this.drawerTitle;

    return drawer;
  }

  get charmDrawer() {
    return this.getAttribute('charm-drawer');
  }

  /**
   * Subscribes to the {@link event:charm:removed} to disable extra parent slots when needed.
   * @listens charm:removed
   */
  static bindCharmRemovedEvent() {
    document.addEventListener('charm:removed', (evt) => {
      const currentIndex = evt.detail.index;

      const parentOptions = document.querySelectorAll('[data-primary-parent]');
      if (currentIndex === 0) {
        parentOptions.forEach((parent, index) => {
          const parentButtonToggle = parent.querySelector(
            '[data-charm-parent-button]',
          );
          if (index !== 0) {
            parentButtonToggle.setAttribute('disabled', 'disabled');
            parentButtonToggle.setAttribute('tabindex', -1);
          }
        });
      }
    });
  }

  /**
   * Subscribes to the {@link event:charm:selected} to update parent UI state.
   * @listens charm:selected
   */
  static bindCharmSelectedEvent() {
    document.addEventListener('charm:selected', (evt) => {
      const { product, variant } = evt.detail;
      const selectedIndex = evt.detail.index;
      const uniqueId = evt.detail.uniqueId;
      const parentOptions = document.querySelectorAll('[data-primary-parent]');

      // Enable all parent options if first one was selected
      if (selectedIndex === 0) {
        parentOptions.forEach((parent) => {
          const parentButtonToggle = parent.querySelector(
            '[data-charm-parent-button]',
          );
          parentButtonToggle.removeAttribute('disabled');
          parentButtonToggle.setAttribute('tabindex', 0);
        });
      }

      // Update label and selected state
      const selectedParent = Array.from(parentOptions).find(
        (parent) => parent.getAttribute('data-unique-id') === uniqueId,
      );
      const parentButtonToggle = selectedParent?.querySelector(
        '[data-charm-parent-button]',
      );

      if (selectedParent) {
        setTimeout(() => {
          parentButtonToggle.focus();
        }, 10);

        selectedParent.classList.add('selected');
        if (selectedParent.productTitleLabel) {
          selectedParent.productTitleLabel.textContent = `${product.title} \u2013 ${variant.title}`;
        }

        if (selectedParent.productPriceLabel) {
          selectedParent.productPriceLabel.textContent =
            CharmParentOption.formatPrice(variant.price);
        }
      }
    });
  }

  static formatPrice(price) {
    return `+${price}`;
  }
}

function initialiseParentOptionIds() {
  const parentOptions = document.querySelectorAll(
    'charm-parent-option[data-primary-parent]',
  );
  parentOptions.forEach((parent) => {
    if (!parent.hasAttribute('data-unique-id')) {
      parent.setAttribute('data-unique-id', crypto.randomUUID());
    }
  });
}

if (!window.__parentOptionIdsInitialised) {
  initialiseParentOptionIds();
  window.__parentOptionIdsInitialised = true;
}

// Initialise event binding once
if (!window.__charmSelectedBound) {
  CharmParentOption.bindCharmSelectedEvent();
  window.__charmSelectedBound = true;
}

if (!window.__charmRemovedBound) {
  CharmParentOption.bindCharmRemovedEvent();
  window.__charmRemovedBound = true;
}

if (!customElements.get('charm-parent-option')) {
  customElements.define('charm-parent-option', CharmParentOption);
}

/**
 * Displays a group of charms and opens the corresponding drawer content.
 * @class
 * @extends HTMLElement
 */
class CharmGroupOption extends HTMLElement {
  constructor() {
    super();
    this.buttonToggle = this.querySelector('[data-charm-group-button]');
    this.productTitleLabel = this.querySelector('[data-charm-label]');
    this.drawerTitle = this.productTitleLabel?.textContent;
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.buttonToggle.addEventListener('click', this.handleClick);
  }

  handleClick() {
    let drawer =
      this.charmBuilder?.querySelector(`[data-id="${this.charmDrawer}"]`) ||
      document.querySelector(`[data-id="${this.charmDrawer}"]`);

    if (!drawer) {
      drawer = this.createDrawer();
    }

    if (this.hasAttribute('show-back')) {
      const backButton = drawer.querySelector('[data-action="close-single"]');
      if (backButton) backButton.hidden = false;
    }

    drawer.open = true;
  }

  createDrawer() {
    const wrapperTemplate = document.getElementById('drawer-wrapper-template');
    const contentTemplate = document.getElementById(this.charmDrawer);
    if (!wrapperTemplate || !contentTemplate) return;

    const wrapperClone = wrapperTemplate.content.cloneNode(true);
    document.body.appendChild(wrapperClone);

    const drawer = document.querySelector('charm-drawer:last-of-type');

    drawer.dataset.id = this.charmDrawer;

    const contentClone = contentTemplate.content.cloneNode(true);

    drawer.querySelector('[data-drawer-content]').appendChild(contentClone);
    drawer.querySelector('[data-drawer-title]').textContent = this.drawerTitle;

    const currentFilter = CharmFilter.getCurrentFilter();

    if (currentFilter !== null) {
      const matchingRadio = drawer.querySelector(
        `input[data-filter-option][value="${currentFilter}"]`,
      );

      if (matchingRadio) {
        matchingRadio.checked = true;
      }
    }

    return drawer;
  }

  get charmDrawer() {
    return this.getAttribute('charm-drawer');
  }
}

if (!customElements.get('charm-group-option')) {
  customElements.define('charm-group-option', CharmGroupOption);
}

/**
 * Radio-like input representing a selectable charm variant.
 * Maintains global selection state and updates pricing/cart inputs.
 * @class CharmOption
 * @extends HTMLInputElement
 * @fires charm:selected
 */
class CharmOption extends HTMLInputElement {
  static selectedCharms = [];
  static limitReached = false;
  static charmCartInput = document.getElementById('CharmItems');
  static primaryBundleReferenceInput = document.getElementById(
    'PrimaryBundleReference',
  );
  static defaultBundleReference = CharmOption.primaryBundleReferenceInput.value;

  constructor() {
    super();

    this.productFormId = document.querySelector('charm-builder')?.dataset.form;
    this.scriptTag = document.getElementById('selected-charms');
    this.charmProductData = JSON.parse(this.dataset.productData);
    this.drawerIndex = parseInt(this.dataset.index);
    this.selectedCharmsHolder = document.getElementById('SelectedCharms');
    this.parentLabel = this.closest('label');
    this.charmBuilder = document.querySelector('charm-builder');
    this.charmLimit = CharmBuilder.getCharmLimit();

    this.handleSelection = this.handleSelection.bind(this);
  }

  connectedCallback() {
    this.addEventListener('change', this.handleSelection);
    this.parentLabel.addEventListener('keydown', (evt) => {
      if (evt.key === ' ' || evt.key === 'Enter') {
        evt.preventDefault();
        this.click(); // Select the radio
      }
    });
  }

  static updateCharmPrices() {
    const productPriceElements = document.querySelectorAll('[data-price]');

    const totalPrice = CharmOption.selectedCharms.reduce((sum, charm) => {
      return sum + charm.price;
    }, 0);

    const productTotalPrice =
      totalPrice + CharmOption.selectedVariantJson.price;

    productPriceElements.forEach((priceEl) => {
      priceEl.textContent = formatMoney(productTotalPrice, currencyFormat);
    });
  }

  handleSelection() {
    const { product, variant } = this.charmProductData;
    let uniqueId;

    // Find the parent option
    const parentOption = document.querySelector(
      `charm-parent-option[data-index="${this.drawerIndex}"]`,
    );

    if (parentOption && parentOption.hasAttribute('data-unique-id')) {
      uniqueId = parentOption.getAttribute('data-unique-id');
    } else {
      uniqueId = crypto.randomUUID();
    }

    const isNewSelection = !parentOption.classList.contains('selected');

    this.selectCharm(uniqueId, product, variant);
    this.createSelectedCharmElement(uniqueId, this.drawerIndex, variant);
    this.updateJsonElement();
    this.dispatchCharmSelectedEvent(uniqueId, product, variant);

    if (isNewSelection && CharmOption.selectedCharms.length < this.charmLimit) {
      CharmOption.buildNewCharmSelector(CharmOption.selectedCharms.length - 1);
    }

    CharmOption.updateCharmPrices();
    CharmOption.updateCharmCartInput();
    CharmBuilder.updateCounter(CharmOption.selectedCharms.length);

    // If we just reached the limit
    if (CharmOption.selectedCharms.length === this.charmLimit) {
      CharmOption.limitReached = true;
    }
  }

  static updateCharmCartInput() {
    this.charmCartInput.setAttribute(
      'data-included-charms',
      CharmOption.selectedCharms.map((charm) => charm.productId),
    );

    const newBundleReference = `${this.defaultBundleReference}|${CharmOption.selectedCharms.map((charm) => charm.productId).join(',')}`;

    this.primaryBundleReferenceInput.value = newBundleReference;
  }

  static buildNewCharmSelector(index) {
    const charmBuilder = document.querySelector('[data-charm-builder]');
    const selectorTemplate = document.getElementById(
      'CharmParentOptionTemplate',
    ).content;
    const nextIndex = index + 1;
    const selectorStrings = JSON.parse(
      document.getElementById('SelectorStrings').textContent,
    );

    const wrapperClone = selectorTemplate.cloneNode(true);
    const charmParentOption = wrapperClone.querySelector('charm-parent-option');

    charmParentOption.setAttribute('charm-drawer', `charm-drawer-${nextIndex}`);
    charmParentOption.querySelector('[data-charm-parent-button]').ariaControls =
      `charm-drawer-${nextIndex}`;

    charmParentOption.dataset.index = nextIndex;

    charmParentOption.setAttribute('data-unique-id', crypto.randomUUID());

    // Create slot content directly
    if (charmParentOption.hasAttribute('data-primary-parent')) {
      const labelSlot = charmParentOption.querySelector('[data-charm-label]');
      labelSlot.textContent = selectorStrings[nextIndex].charmOrdinal;

      const priceSlot = charmParentOption.querySelector('[data-charm-price]');
      priceSlot.textContent = selectorStrings[nextIndex].charmPrice;
    }

    charmBuilder.appendChild(wrapperClone);
  }

  /**
   * Dispatches a {@link event:charm:selected} with selection payload.
   * @param {string} uniqueId
   * @param {object} product
   * @param {object} variant
   * @fires charm:selected
   */
  dispatchCharmSelectedEvent(uniqueId, product, variant) {
    const selectedCharmEvent = new CustomEvent('charm:selected', {
      bubbles: true,
      detail: {
        uniqueId: uniqueId,
        index: this.drawerIndex,
        variant: variant,
        product: product,
      },
    });

    document.dispatchEvent(selectedCharmEvent);
  }

  selectCharm(uniqueId, productData, variantData) {
    const existingIndex = CharmOption.selectedCharms.findIndex(
      (charm) => charm.id === uniqueId,
    );

    const charmObject = {
      id: uniqueId,
      title: productData.title,
      variantId: variantData.id,
      productId: productData.id,
      price: variantData.priceRaw,
      groupId: this.dataset.charmGroupId,
    };

    if (existingIndex !== -1) {
      // Update existing selection
      CharmOption.selectedCharms[existingIndex] = charmObject;
    } else {
      // Add new selection
      CharmOption.selectedCharms.push(charmObject);
    }

    CharmOption.updateCharmPrices();
    CharmOption.toggleFormVisibility();
  }

  static toggleFormVisibility() {
    const productForms = document.querySelectorAll(
      '.product-form__payment-container, .product-sticky-form__payment-container',
    );

    const charmPrimaryButtons = document.querySelectorAll(
      '[data-charm-button]',
    );

    if (CharmOption.selectedCharms.length === 0) {
      productForms.forEach((form) => (form.hidden = true));
      charmPrimaryButtons.forEach((form) => (form.style.display = 'block'));
    } else {
      productForms.forEach((form) => (form.hidden = false));
      charmPrimaryButtons.forEach((form) => (form.style.display = 'none'));
    }
  }

  createSelectedCharmElement(uniqueId, index, variant) {
    // Prevent duplicate selected charm elements for the same uniqueId
    const existingCharmElement = this.selectedCharmsHolder.querySelector(
      `selected-charm[data-unique-id="${uniqueId}"]`,
    );

    const templateId = `selected-charm-template--${variant.id}-${index}`;
    const template = document.getElementById(templateId);
    if (!template) return;

    const selectedCharmElement = template.content.cloneNode(true);
    if (existingCharmElement) {
      const newCharmElement =
        selectedCharmElement.querySelector('selected-charm');
      if (newCharmElement) {
        newCharmElement.setAttribute('data-unique-id', uniqueId);
      }

      // Replace the parent wrapper, not the web component directly
      this.selectedCharmsHolder.replaceChild(
        selectedCharmElement,
        existingCharmElement.parentElement,
      );
    } else {
      const newCharmElement =
        selectedCharmElement.querySelector('selected-charm');
      if (newCharmElement) {
        newCharmElement.setAttribute('data-unique-id', uniqueId);
      }

      this.selectedCharmsHolder.appendChild(selectedCharmElement);
    }

    if (index === 0) {
      this.selectedCharmsHolder.ariaHidden = false;
    }
  }

  static get selectedVariantJson() {
    return JSON.parse(document.getElementById('ProductData').textContent);
  }

  updateJsonElement() {
    this.scriptTag.textContent = JSON.stringify({
      selectedCharms: CharmOption.selectedCharms,
    });
  }
}

if (!customElements.get('charm-option')) {
  customElements.define('charm-option', CharmOption, { extends: 'input' });
}

/**
 * Visual card for a selected charm; supports remove & re-edit actions.
 * @class SelectedCharm
 * @extends HTMLElement
 * @fires charm:removed
 */
class SelectedCharm extends HTMLElement {
  constructor() {
    super();

    this.removeButton = this.querySelector('[data-charm-remove]');
    this.scriptTag = document.getElementById('selected-charms');
    this.selectedCharmsHolder = document.getElementById('SelectedCharms');
    this.charmIndex = parseInt(this.dataset.index);
    this.handleRemove = this.handleRemove.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', (evt) => {
      const drawerId = evt.target.getAttribute('aria-controls');

      this.triggerDrawerOnClick(drawerId);
    });

    if (this.removeButton) {
      this.removeButton.addEventListener('click', (evt) => {
        evt.stopPropagation();
        this.handleRemove();
      });

      this.removeButton.addEventListener('keydown', (evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
          evt.stopPropagation();
          this.handleRemove();
        }
      });
    }

    this.addEventListener('keydown', (evt) => {
      if (evt.key === ' ' || evt.key === 'Enter') {
        this.triggerDrawerOnClick();
      }
    });
  }

  triggerDrawerOnClick(drawerId) {
    const drawer = document.querySelector(`[data-id="${drawerId}"]`);

    if (drawer) {
      drawer.open = true;
      drawer.focus();
    }
  }

  /**
   * Removes a selected charm and reindexes remaining selections.
   * Dispatches {@link event:charm:removed} on completion.
   * @fires charm:removed
   */
  handleRemove() {
    const uniqueId = this.getAttribute('data-unique-id');
    const selectorStrings = JSON.parse(
      document.getElementById('SelectorStrings').textContent,
    );
    // Remove from static selectedCharms array
    CharmOption.selectedCharms = CharmOption.selectedCharms.filter(
      (charm) => charm.id !== uniqueId,
    );

    if (CharmOption.selectedCharms.length > 0) {
      CharmOption.selectedCharms[0].firstCharm = true;
    }

    CharmOption.updateCharmPrices();

    // Update the script tag JSON
    this.scriptTag.textContent = JSON.stringify({
      selectedCharms: CharmOption.selectedCharms,
    });

    // Remove the parent option linked to this uniqueId
    const parentOption = document.querySelector(
      `charm-parent-option[data-unique-id="${uniqueId}"]`,
    );
    if (parentOption) {
      parentOption.remove();
    }

    // Reindex remaining parent options
    const remainingParents = document.querySelectorAll('charm-parent-option');
    remainingParents.forEach((parent, newIndex) => {
      parent.dataset.index = newIndex;
      parent.setAttribute('charm-drawer', `charm-drawer-${newIndex}`);

      const label = parent.querySelector('[data-charm-label]');
      if (
        label &&
        !parent.classList.contains('selected') &&
        selectorStrings[newIndex]
      ) {
        label.textContent = selectorStrings[newIndex].charmOrdinal;
      }

      const button = parent.querySelector('[data-charm-parent-button]');
      if (button) {
        button.setAttribute('aria-controls', `charm-drawer-${newIndex}`);
      }
    });

    CharmOption.updateCharmCartInput();
    CharmOption.updateCharmPrices();
    CharmBuilder.updateCounter(CharmOption.selectedCharms.length);

    // Remove the element from the DOM with animation
    this.classList.add('removing');
    this.parentElement.addEventListener(
      'transitionend',
      () => {
        this.parentElement.remove();
        this.checkIfEmpty();

        const remainingDrawers = document.querySelectorAll(
          '[data-drawer-index]',
        );

        remainingDrawers.forEach((drawer, newIndex) => {
          drawer.dataset.drawerIndex = newIndex;
        });

        const remainingSelectedCharms =
          document.querySelectorAll('selected-charm');
        remainingSelectedCharms.forEach((selected, newIndex) => {
          selected.setAttribute('data-drawer-index', newIndex);
          selected.setAttribute('data-index', newIndex);
          selected.setAttribute('aria-controls', `charm-drawer-${newIndex}`);

          this.updateCharmRadios(newIndex);
        });
      },
      { once: true },
    );

    CharmOption.toggleFormVisibility();

    // If the limit was previously reached, and now we've removed one
    if (CharmOption.limitReached) {
      const parentOptions = document.querySelectorAll(
        'charm-parent-option[data-primary-parent]',
      );
      const newIndex = parentOptions.length - 1;

      CharmOption.buildNewCharmSelector(newIndex);
      // Reset the limit flag now that we're under the limit
      CharmOption.limitReached = false;
    }

    // Fire removed event (index is now irrelevant, but we keep for compatibility)
    const removedCharmEvent = new CustomEvent('charm:removed', {
      bubbles: true,
      detail: {
        index: -1,
      },
    });
    document.dispatchEvent(removedCharmEvent);
  }

  updateCharmRadios(index) {
    const allRadioInputs = document.querySelectorAll(
      'input[is="charm-option"]',
    );

    // Only deselect the radio linked to the removed charm instead of unrelated radios
    allRadioInputs.forEach((input) => {
      const drawer = input.getAttribute('data-drawer');
      const groupId = input.getAttribute('data-charm-group-id');
      const variantId = input.value;

      if (
        input.checked &&
        drawer === `charm-drawer-${index}` &&
        groupId === this.getAttribute('data-drawer-index') &&
        variantId ===
          this.getAttribute('aria-controls').replace('charm-drawer-', '')
      ) {
        input.checked = false;
      }
    });

    const selectedCharm = CharmOption.selectedCharms[this.charmIndex];

    if (selectedCharm) {
      const drawerId = `charm-drawer-${selectedCharm.drawerIndex}`;

      // 1. Try to find the radio in the live drawer first
      const radioToSelect = document.querySelector(
        `input[is="charm-option"][data-drawer="${drawerId}"][data-charm-group-id="${selectedCharm.groupId}"][value="${selectedCharm.variantId}"]`,
      );

      if (radioToSelect) {
        // 2. The drawer exists, select the radio directly
        radioToSelect.checked = true;
        radioToSelect.focus();
      } else {
        // 3. Drawer not created yet, update the template
        const templateId = `${selectedCharm.groupId}-${selectedCharm.drawerIndex}`;
        const template = document.getElementById(templateId);

        if (template) {
          const templateRadio = template.content.querySelector(
            `input[is="charm-option"][data-charm-group-id="${selectedCharm.groupId}"][value="${selectedCharm.variantId}"]`,
          );

          if (templateRadio) {
            templateRadio.setAttribute('checked', 'checked');
          }
        }
      }
    }
  }

  checkIfEmpty() {
    if (this.selectedCharmsHolder.children.length === 0) {
      this.selectedCharmsHolder.innerHTML = '';
      this.selectedCharmsHolder.ariaHidden = true;
    }
  }
}

if (!customElements.get('selected-charm')) {
  customElements.define('selected-charm', SelectedCharm);
}


/**
 * Drawer-scoped filter controller (e.g., color); syncs session & cart attributes.
 * @class
 * @extends HTMLElement
 */
class CharmFilter extends HTMLElement {
  constructor() {
    super();
    this.colorlabel = this.querySelector('[data-color-name]');
    this.radioOptions = this.querySelectorAll('[data-filter-option]');
    this.filterAnnouncement = this.querySelector('[data-filter-announcement]');
    this.filterAnnouncementNewLabel =
      translationStrings.customiser.filterApplied;
    this.charmGrid = this.closest('[data-drawer-content]').querySelector(
      '[data-charm-grid]',
    );
  }

  async connectedCallback() {
    const existingFilter = await this.checkForExistingFilter();

    this.radioOptions.forEach((radio) => {
      radio.addEventListener('change', async (evt) => {
        const inputValue = evt.target.value;

        this.updateColorLabel(inputValue);
        this.filterGridItems(inputValue);

        if (this.filterAnnouncement) {
          this.filterAnnouncement.textContent =
            this.filterAnnouncementNewLabel.replace('[color]', inputValue);
        }

        await this.updateFilterValues(inputValue);
      });
    });

    if (!existingFilter) return;

    this.filterGridItems(existingFilter);
    this.updateColorLabel(existingFilter);
  }

  updateColorLabel(value) {
    this.colorlabel.textContent = value;
  }

  async updateFilterValues(value) {
    sessionStorage.setItem('charm_filter', value);

    const filterCartAttributes = {
      attributes: {
        charm_filter: value,
      },
    };

    try {
      await fetch(`${translationStrings.routes.cartUpdateUrl}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(filterCartAttributes),
      })
        .then(async (response) => {
          const responseJson = await response.json();

          return responseJson;
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    } catch (error) {
      console.error('Error:', error);
    }
  }

  async checkForExistingFilter() {
    const storedFilter = sessionStorage.getItem('charm_filter');
    if (storedFilter !== null) return storedFilter;

    try {
      const request = await fetch(`${translationStrings.routes.cartUrl}.js`);
      const response = await request.json();

      if (response.attributes.charm_filter) {
        sessionStorage.setItem(
          'charm_filter',
          response.attributes.charm_filter,
        );
        return response.attributes.charm_filter;
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    }

    return null;
  }

  filterGridItems(value) {
    const filterableItems = this.charmGrid.querySelectorAll(
      '[data-filter-title]',
    );

    filterableItems.forEach((item) => {
      if (item.getAttribute('data-filter-title') === value) {
        item.hidden = false;
      } else {
        item.hidden = true;
      }
    });
  }

  static getCurrentFilter() {
    return sessionStorage.getItem('charm_filter');
  }
}

if (!customElements.get('charm-filter')) {
  customElements.define('charm-filter', CharmFilter);
}
